import { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { 
  Gamepad2, Swords, Wallet, Clock, Shield, Plus, Menu, X,
  User, Settings, LogOut, Send, Target, Trophy, Crown, HelpCircle, Receipt
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { 
  DropdownMenu, 
  DropdownMenuContent, 
  DropdownMenuItem, 
  DropdownMenuTrigger,
  DropdownMenuSeparator
} from '../components/ui/dropdown-menu';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { motion } from 'framer-motion';
import { Toaster } from '../components/ui/sonner';
import NotificationBell from './NotificationBell';
import MatchStakeLogo, { MatchStakeIcon } from './MatchStakeLogo';

const DashboardLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const navItems = [
    { path: '/dashboard', label: 'Dashboard', icon: Gamepad2 },
    { path: '/lobby', label: 'Lobby', icon: Swords },
    { path: '/quick-match', label: 'Quick', icon: Target },
    { path: '/challenge', label: 'Challenge', icon: Send },
    { path: '/leaderboard', label: 'Leaderboard', icon: Trophy },
  ];

  const secondaryItems = [
    { path: '/wallet', label: 'Wallet', icon: Wallet },
    { path: '/transactions', label: 'Transactions', icon: Receipt },
    { path: '/history', label: 'Match History', icon: Clock },
    { path: '/support', label: 'Support', icon: HelpCircle },
  ];

  const isActive = (path) => location.pathname === path;

  return (
    <div className="min-h-screen bg-[#050505]">
      <Toaster position="top-right" richColors />
      
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 glass border-b border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-3 flex items-center justify-between">
          <div className="flex items-center gap-6">
            <Link to="/" className="flex items-center gap-2">
              <MatchStakeIcon className="w-8 h-8" />
              <span className="font-heading font-bold text-lg tracking-tight hidden sm:block">MATCHSTAKE</span>
            </Link>
            <nav className="hidden xl:flex items-center gap-0.5">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  className={`flex items-center gap-1.5 px-2.5 py-2 rounded-lg transition-colors text-sm font-medium ${
                    isActive(item.path) 
                      ? 'bg-white/5 text-white' 
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon className="w-3.5 h-3.5" />
                  {item.label}
                </Link>
              ))}
              
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-1 px-2.5 py-2 text-white/70 hover:text-white hover:bg-white/5 h-auto font-medium text-sm">
                    <Menu className="w-3.5 h-3.5 mr-1" />
                    More
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48 bg-[#0A0A0B] border-white/10">
                  {secondaryItems.map(item => (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className={`cursor-pointer ${isActive(item.path) ? 'text-[#00F0FF] bg-white/5' : ''}`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                  {user?.is_admin && (
                    <>
                      <DropdownMenuSeparator className="bg-white/10" />
                      <DropdownMenuItem 
                        onClick={() => navigate('/admin')}
                        className={`cursor-pointer text-[#FF2E2E] ${isActive('/admin') ? 'bg-[#FF2E2E]/10' : ''}`}
                      >
                        <Shield className="w-4 h-4 mr-2" />
                        Admin Panel
                      </DropdownMenuItem>
                    </>
                  )}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
            
            {/* Tablet/Small Desktop view - even more consolidated */}
            <nav className="hidden md:flex xl:hidden items-center gap-1">
              <Link
                to="/dashboard"
                className={`flex items-center gap-2 px-3 py-2 rounded-lg transition-colors text-sm ${
                  isActive('/dashboard') ? 'bg-white/5 text-white' : 'text-white/70 hover:text-white'
                }`}
              >
                <Gamepad2 className="w-4 h-4" />
                Dashboard
              </Link>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-3 py-2 text-white/70 hover:text-white h-auto text-sm">
                    <Menu className="w-4 h-4" />
                    Menu
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-56 bg-[#0A0A0B] border-white/10">
                  {navItems.slice(1).concat(secondaryItems).map(item => (
                    <DropdownMenuItem 
                      key={item.path} 
                      onClick={() => navigate(item.path)}
                      className={`cursor-pointer ${isActive(item.path) ? 'text-[#00F0FF] bg-white/5' : ''}`}
                    >
                      <item.icon className="w-4 h-4 mr-2" />
                      {item.label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </nav>
          </div>

          <div className="flex items-center gap-4">
            <Button 
              onClick={() => navigate('/season-pass')}
              className="hidden sm:flex items-center gap-2 px-3 py-2 bg-gradient-to-r from-[#FFD700]/20 to-[#FFD700]/10 border border-[#FFD700]/30 text-[#FFD700] hover:bg-[#FFD700]/20"
              data-testid="header-season-pass-btn"
            >
              <Crown className="w-4 h-4" /> Pass
            </Button>
            <Button 
              onClick={() => navigate('/create-match')}
              className="btn-primary clip-slant-sm px-4 py-2 hidden sm:flex"
              data-testid="header-create-match-btn"
            >
              <Plus className="w-4 h-4 mr-1" /> New Match
            </Button>

            <NotificationBell />

            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button variant="ghost" className="flex items-center gap-2 px-2" data-testid="header-user-menu">
                  <Avatar className="w-8 h-8">
                    <AvatarImage src={user?.picture} />
                    <AvatarFallback className="bg-[#7000FF] text-white text-xs">
                      {user?.name?.charAt(0) || 'U'}
                    </AvatarFallback>
                  </Avatar>
                  <span className="hidden sm:block text-sm">{user?.name?.split(' ')[0]}</span>
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48 bg-[#0A0A0B] border-white/10">
                <DropdownMenuItem onClick={() => navigate('/profile')} className="cursor-pointer">
                  <User className="w-4 h-4 mr-2" /> Profile
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => navigate('/settings')} className="cursor-pointer">
                  <Settings className="w-4 h-4 mr-2" /> Settings
                </DropdownMenuItem>
                <DropdownMenuSeparator className="bg-white/10" />
                <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400">
                  <LogOut className="w-4 h-4 mr-2" /> Logout
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            <Button 
              variant="ghost" 
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <motion.div 
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            className="md:hidden border-t border-white/10 bg-[#050505]"
          >
            <nav className="flex flex-col p-4 gap-2">
              {navItems.map(item => (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`flex items-center gap-2 px-4 py-2 rounded-lg transition-colors ${
                    isActive(item.path) 
                      ? 'bg-white/5 text-white' 
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`}
                >
                  <item.icon className="w-4 h-4" />
                  {item.label}
                </Link>
              ))}
              {user?.is_admin && (
                <Link
                  to="/admin"
                  onClick={() => setMobileMenuOpen(false)}
                  className="flex items-center gap-2 px-4 py-2 rounded-lg text-[#FF2E2E]/70 hover:text-[#FF2E2E]"
                >
                  <Shield className="w-4 h-4" />
                  Admin
                </Link>
              )}
              <Button 
                onClick={() => { navigate('/create-match'); setMobileMenuOpen(false); }}
                className="btn-primary clip-slant-sm mt-2"
              >
                <Plus className="w-4 h-4 mr-1" /> New Match
              </Button>
              <Button 
                onClick={() => { logout(); setMobileMenuOpen(false); }}
                variant="outline"
                className="mt-2 text-red-400 border-red-400/30 hover:bg-red-400/10"
                data-testid="mobile-logout-btn"
              >
                <LogOut className="w-4 h-4 mr-1" /> Logout
              </Button>
            </nav>
          </motion.div>
        )}
      </header>

      {/* Main Content */}
      <main className="pt-24 pb-10 px-4">
        {children}
      </main>
    </div>
  );
};

export default DashboardLayout;
